// src/pages/Users/News/components/NewsToolbar.jsx

import React, { useMemo, useState } from "react";
import {
  FaChevronDown,
  FaChevronUp,
  FaEye,
  FaPlus,
  FaRedo,
  FaSearch,
  FaSlidersH,
} from "react-icons/fa";

import {
  DEFAULT_NEWS_UI,
  hasActiveNewsFilters,
} from "../utils/newsUtils";

export default function NewsToolbar({
  ui,
  searchInput,
  setSearchInput,
  isManager,
  filteredCount,
  totalCount,
  onChange,
  onReset,
  onCreate,
}) {
  const [filtersOpen, setFiltersOpen] = useState(false);

  const activeFilters = useMemo(
    () => hasActiveNewsFilters(ui, isManager),
    [ui, isManager]
  );

  return (
    <div className="rounded-[1.75rem] border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-4">
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-400" />

          <input
            type="text"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Rechercher une actualité..."
            className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-400/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
          />
        </div>

        {isManager && (
          <button
            type="button"
            onClick={onCreate}
            className="hidden h-12 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 text-sm font-bold text-white shadow-sm transition active:scale-95 dark:bg-white dark:text-slate-950 sm:inline-flex"
          >
            <FaPlus />
            Actu
          </button>
        )}

        <button
          type="button"
          onClick={() => setFiltersOpen((prev) => !prev)}
          className={`inline-flex h-12 items-center justify-center gap-2 rounded-2xl px-4 text-sm font-bold shadow-sm transition active:scale-95 md:hidden ${
            filtersOpen
              ? "bg-orange-50 text-orange-700 ring-1 ring-orange-200 dark:bg-orange-950/40 dark:text-orange-200 dark:ring-orange-900"
              : "bg-slate-950 text-white dark:bg-white dark:text-slate-950"
          }`}
        >
          <FaSlidersH />
          <span>Filtres</span>
          {filtersOpen ? (
            <FaChevronUp className="text-xs" />
          ) : (
            <FaChevronDown className="text-xs" />
          )}
        </button>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 md:hidden">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
          {filteredCount} / {totalCount} article{totalCount > 1 ? "s" : ""}
        </p>

        <div className="flex items-center gap-2">
          {activeFilters && (
            <button
              type="button"
              onClick={onReset}
              className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
            >
              <FaRedo className="text-[11px]" />
              Reset
            </button>
          )}

          {isManager && (
            <button
              type="button"
              onClick={onCreate}
              className="inline-flex items-center gap-2 rounded-full bg-slate-950 px-3 py-2 text-xs font-bold text-white transition active:scale-95 dark:bg-white dark:text-slate-950 sm:hidden"
            >
              <FaPlus className="text-[11px]" />
              Actu
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 hidden grid-cols-3 gap-3 md:grid">
        <FiltersContent ui={ui} isManager={isManager} onChange={onChange} />

        <div className="flex items-end justify-end">
          {activeFilters && (
            <button
              type="button"
              onClick={onReset}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 text-sm font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
            >
              <FaRedo />
              Réinitialiser
            </button>
          )}
        </div>
      </div>

      {filtersOpen && (
        <div className="mt-3 md:hidden">
          <div className="max-h-[calc(100dvh-180px)] overflow-y-auto rounded-[1.5rem] border border-slate-200 bg-slate-50 p-3 shadow-inner dark:border-slate-800 dark:bg-slate-950">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">
                  Affichage
                </p>
                <h3 className="text-sm font-bold text-slate-950 dark:text-white">
                  Filtrer les actualités
                </h3>
              </div>

              {activeFilters && (
                <button
                  type="button"
                  onClick={onReset}
                  className="rounded-full bg-white px-3 py-2 text-xs font-bold text-slate-700 ring-1 ring-slate-200 transition active:scale-95 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700"
                >
                  Réinitialiser
                </button>
              )}
            </div>

            <div className="space-y-3">
              <FiltersContent
                ui={ui}
                isManager={isManager}
                onChange={onChange}
                stacked
              />
            </div>

            <button
              type="button"
              onClick={() => setFiltersOpen(false)}
              className="mt-4 flex min-h-12 w-full items-center justify-center rounded-2xl bg-slate-950 px-4 text-sm font-bold text-white transition active:scale-95 dark:bg-white dark:text-slate-950"
            >
              Voir les actualités
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FiltersContent({ ui, isManager, onChange, stacked = false }) {
  const wrapperClass = stacked ? "space-y-3" : "contents";

  return (
    <div className={wrapperClass}>
      <label className="block">
        <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
          Trier par
        </span>

        <select
          value={ui.sort}
          onChange={(event) =>
            onChange({
              sort: event.target.value,
              perPage: DEFAULT_NEWS_UI.perPage,
            })
          }
          className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 text-sm font-bold text-slate-800 outline-none transition focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-400/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
        >
          <option value="dateDesc">Plus récentes</option>
          <option value="dateAsc">Plus anciennes</option>
          <option value="alpha">Titre A-Z</option>
        </select>
      </label>

      {isManager && (
        <label className="block">
          <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
            Visibilité
          </span>

          <button
            type="button"
            onClick={() =>
              onChange({
                showInactive: !ui.showInactive,
                perPage: DEFAULT_NEWS_UI.perPage,
              })
            }
            className={`flex h-12 w-full items-center justify-between rounded-2xl border px-3 text-left text-sm font-bold transition active:scale-[0.99] ${
              ui.showInactive
                ? "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200"
                : "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
            }`}
          >
            <span className="inline-flex items-center gap-2">
              <FaEye />
              {ui.showInactive ? "Tous les articles" : "Articles actifs"}
            </span>

            <span
              className={`h-5 w-9 rounded-full p-0.5 transition ${
                ui.showInactive
                  ? "bg-amber-500"
                  : "bg-slate-300 dark:bg-slate-700"
              }`}
            >
              <span
                className={`block h-4 w-4 rounded-full bg-white transition ${
                  ui.showInactive ? "translate-x-4" : ""
                }`}
              />
            </span>
          </button>
        </label>
      )}
    </div>
  );
}